import { motion, useScroll, useTransform } from 'framer-motion';
import { UtensilsCrossed, Salad, Coffee, Flame } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { useRef } from 'react';
import { categoryImages, DOORDASH_URL, UBEREATS_URL } from '../data/menu';

const categoryIcons = [Flame, UtensilsCrossed, Salad, Coffee];

const cardVariants = {
  hidden: { opacity: 0, scale: 0.8, rotateY: -30 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    rotateY: 0,
    transition: {
      delay: i * 0.15,
      duration: 0.6,
      type: "spring",
      stiffness: 100
    }
  })
};

export function MenuPreview() {
  const { t, isRTL } = useLanguage();
  const categories = t('menu.categories');
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section 
      ref={ref}
      id="menu" 
      className="relative py-24 sm:py-32 bg-[#6B1C23] overflow-hidden"
      data-testid="menu-preview-section"
    >
      {/* Animated Background Pattern */}
      <motion.div 
        className="absolute inset-0 opacity-10"
        style={{ y: backgroundY }}
      >
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </motion.div>

      {/* Static floating elements */}
      {[...Array(4)].map((_, i) => (
        <div
          key={`float-${i}`}
          className="absolute w-32 h-32 border border-white/10 rounded-full"
          style={{
            left: `${10 + i * 25}%`,
            top: `${20 + (i % 2) * 40}%`,
            opacity: 0.12,
          }}
        />
      ))}

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.h2 
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 100 }}
            className={`font-cormorant text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4 ${
              isRTL ? 'font-noto-serif-arabic' : ''
            }`}
            data-testid="menu-title"
          >
            {t('menu.title')}
          </motion.h2>
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "100px" }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="h-1 bg-white/50 mx-auto mb-4"
          />
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className={`font-manrope text-white/80 text-lg ${isRTL ? 'font-cairo' : ''}`}
          >
            {t('menu.subtitle')}
          </motion.p>
        </motion.div>

        {/* Categories Grid with 3D Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12" style={{ perspective: "1000px" }}>
          {categories.map((category, index) => {
            const Icon = categoryIcons[index];
            return (
              <motion.a
                key={`cat-${index}`}
                href={DOORDASH_URL}
                target="_blank"
                rel="noopener noreferrer"
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
                whileHover={{ 
                  scale: 1.05, 
                  rotateY: 5,
                  z: 50,
                  transition: { duration: 0.3 }
                }}
                className="group relative h-48 sm:h-64 overflow-hidden cursor-pointer rounded-sm"
                data-testid={`menu-category-${index}`}
                style={{ transformStyle: "preserve-3d" }}
              >
                <motion.img
                  src={categoryImages[index]}
                  alt={category}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.2 }}
                  transition={{ duration: 0.6 }}
                />
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"
                  whileHover={{ opacity: 0.9 }}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    whileInView={{ scale: 1, rotate: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + index * 0.1, type: "spring" }}
                    whileHover={{ rotate: 360, scale: 1.2 }}
                  >
                    <Icon className="w-8 h-8 text-white mb-3 opacity-90" />
                  </motion.div>
                  <motion.h3 
                    className={`font-cormorant text-xl sm:text-2xl font-semibold text-white ${
                      isRTL ? 'font-noto-serif-arabic' : ''
                    }`}
                    whileHover={{ scale: 1.1 }}
                  >
                    {category}
                  </motion.h3>
                </div>
                {/* Animated Border */}
                <motion.div 
                  className="absolute inset-0 border-2 border-white/0 rounded-sm"
                  whileHover={{ borderColor: "rgba(255,255,255,0.8)" }}
                  transition={{ duration: 0.3 }}
                />
                {/* Shine Effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full"
                  whileHover={{ translateX: "200%" }}
                  transition={{ duration: 0.8 }}
                />
              </motion.a>
            );
          })}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center flex flex-col sm:flex-row gap-4 items-center justify-center"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              asChild
              size="lg"
              className="bg-white text-[#6B1C23] hover:bg-gray-100 transition-all px-8 py-6 text-lg font-manrope shadow-lg"
              data-testid="menu-doordash-btn"
            >
              <a href={DOORDASH_URL} target="_blank" rel="noopener noreferrer">
                {t('menu.viewFull')} — DoorDash
              </a>
            </Button>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              asChild
              size="lg"
              className="bg-[#142328] text-white hover:bg-[#1f3640] transition-all px-8 py-6 text-lg font-manrope shadow-lg"
              data-testid="menu-ubereats-btn"
            >
              <a href={UBEREATS_URL} target="_blank" rel="noopener noreferrer">
                {t('menu.viewFull')} — Uber Eats
              </a>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
